import type { NexusHandler } from "@otto-ec/global-resources/nexus";
import { dispatchSheetEvent } from "../event/event.dispatch";
import { popStack, popStackAll, pushStack, setInfo } from "../stack/stack.global.svelte.js";
import { findInstance, getInstance, stackScope } from "../stack/stack.utils.js";
import { BEFORE_CLOSE, BEFORE_OPEN } from "../event/event.name.defs";
import { SHEET_ACTION_PROGRAMMATICALLY } from "../event/event.action.defs";
import { preflightPop, preflightPush } from "../stack/stack.preflight.svelte.js";
import { applyProps } from "../utils/props";
import { sheetTagName } from "../utils/consts";
import type { SheetV1 } from "../index.js";

const log = /*           */ stackScope.scope("methods");

/**
 *
 */
export const sheetCreate = ((config = {}, options = {}) => {
  /*                                             */
  let sheetElement = config.id ? findInstance(config.id, options.container ?? undefined) : null;
  if (!sheetElement) {
    sheetElement = document.createElement(sheetTagName);
    /*                                                         */
    sheetElement.style.display = "none";
  }

  /*                                              */
  const { dataSet = {}, ...conf } = config;
  const { adjacentElement } = options;
  const container = options.container ?? document.body;

  /*                                             */
  applyProps(sheetElement, conf);

  Object.entries(dataSet).forEach(([key, value]) => {
    sheetElement.dataset[key] = value;
  });

  /*                                                         */
  if (adjacentElement) {
    adjacentElement.insertAdjacentElement("afterend", sheetElement);
    return sheetElement;
  }

  /*                                                            */
  container.append(sheetElement as Node);
  setInfo(options.info);
  return sheetElement;
}) satisfies NexusHandler<SheetV1["create"]>;

/**
 *
 */
export const sheetOpenById = ((sheetId, options = {}) => {
  const { action = SHEET_ACTION_PROGRAMMATICALLY } = options;
  const { previos, isActive, reason } = preflightPush(sheetId);

  /*                                                                */
  if (isActive) return (log.info("Sheet is already open", sheetId), false);

  /*                                                      */
  if (previos && !findInstance(previos)) {
    log.error("Unexpected stack state, previous instance not found", previos);
    return (popStackAll(action, options.info), false);
  }

  const closePayload = { reason, action: action[0], closeType: action[1], info: options.info };
  if (previos && !dispatchSheetEvent(BEFORE_CLOSE, getInstance(previos), closePayload)) {
    return (log.info("Deactivation refused, by", previos, "could not open", sheetId), false);
  }

  /*                                                                          */
  /*                                                     */
  let instance = findInstance(sheetId);

  const openPayload = { reason, action: action[0], id: sheetId, info: options.info };
  if (!dispatchSheetEvent(BEFORE_OPEN, instance, openPayload)) {
    return (log.info("Activation refused, by", sheetId, "could not open", sheetId), false);
  }

  /*                                                            */
  instance ??= findInstance(sheetId);
  if (!instance) {
    return (log.warn("Could not open", sheetId, "instance not found"), false);
  }

  /*                                                                                        */
  log.info("Activating sheet instance", sheetId);
  return (pushStack(sheetId, action, options.info), true);
}) satisfies NexusHandler<SheetV1["open"]>;

/**
 *
 */
export const sheetClose = ((options = {}) => {
  const { action = SHEET_ACTION_PROGRAMMATICALLY } = options;
  const { isActive, reason, current } = preflightPop();

  /*                                  */
  if (!isActive) return (log.info("No sheet is currently open, cannot go back"), false);

  if (getInstance(current).disableCloseInteractions && action !== SHEET_ACTION_PROGRAMMATICALLY)
    return false;

  /*                                                  */
  const payload = { reason, action: action[0], closeType: action[1], info: options.info };
  if (!dispatchSheetEvent(BEFORE_CLOSE, getInstance(current), payload)) {
    return (log.info("Deactivation refused, by", current, "could not go back"), false);
  }

  log.info("Deactivating sheet instance", current);
  return (popStackAll(action, options.info), true);
}) satisfies NexusHandler<SheetV1["close"]>;

/**
 *
 */
export const sheetGoBack = ((options = {}) => {
  const { action = SHEET_ACTION_PROGRAMMATICALLY } = options;
  const { isActive, previos, reason, current } = preflightPop();

  /*                                  */
  if (!isActive) return (log.warn("No sheet is currently open, cannot go back"), false);

  /*                                                               */
  if (!previos) return sheetClose(options);

  if (previos && !findInstance(previos)) {
    log.error("Unexpected stack state, previous instance not found", previos);
    return (popStackAll(action, options.info), false);
  }

  if (
    !dispatchSheetEvent(BEFORE_CLOSE, getInstance(current), {
      reason,
      action: action[0],
      closeType: action[1],
      info: options.info,
    })
  ) {
    return (log.info("Deactivation refused, by", current, "could not go back"), false);
  }

  /*                                                                          */
  /*                                                     */
  if (
    !dispatchSheetEvent(BEFORE_OPEN, getInstance(previos), {
      reason,
      action: action[0],
      info: options.info,
    })
  ) {
    return (log.info("Activation refused, by", previos, "could not open", previos), false);
  }

  log.info("Going back to sheet instance", previos);
  return (popStack(action, options.info), true);
}) satisfies NexusHandler<SheetV1["back"]>;
